export default function PromotionPricing() {
  const plans = [
    {
      name: 'Basic Promotion',
      price: '$25',
      duration: 'per event',
      description: 'Perfect for small community events and gatherings',
      features: [
        'Standard event listing',
        'Event calendar placement',
        'Basic event details page',
        'Social media share buttons',
        'Email confirmation',
        '7-day listing duration'
      ],
      color: 'gray',
      popular: false
    },
    {
      name: 'Featured Promotion',
      price: '$50',
      duration: 'per event',
      description: 'Ideal for concerts, workshops, and ticketed events',
      features: [
        'Everything in Basic',
        '"Featured Event" badge',
        'Homepage carousel placement',
        'Priority search ranking',
        'Highlighted in newsletter',
        'Extended 14-day listing',
        'Event analytics dashboard',
        'Ticket sales integration'
      ],
      color: 'emerald',
      popular: true
    },
    {
      name: 'Premium Promotion',
      price: '$75',
      duration: 'per event',
      description: 'Maximum visibility for major events and festivals',
      features: [
        'Everything in Featured',
        'Top banner placement',
        'Dedicated email blast',
        'Social media promotion',
        '30-day listing duration',
        'Multiple date support',
        'Advanced analytics',
        'Priority customer support',
        'Recurring event discount'
      ],
      color: 'amber',
      popular: false
    }
  ];

  const ticketing = {
    title: 'Ticketing Commission',
    description: 'Sell tickets directly through our platform',
    features: [
      'Secure payment processing',
      'Automated ticket delivery',
      'QR code check-in system',
      'Real-time sales tracking',
      'Customer support included'
    ],
    commission: '5-10%',
    note: 'per ticket sold'
  };

  const sponsorship = {
    title: 'Event Sponsorship Packages',
    description: 'Sponsor major community events for maximum brand exposure',
    tiers: [
      { name: 'Bronze Sponsor', price: '$250', benefits: 'Logo on event page, social media mention' },
      { name: 'Silver Sponsor', price: '$500', benefits: 'All Bronze + newsletter feature, homepage banner' },
      { name: 'Gold Sponsor', price: '$1,000', benefits: 'All Silver + dedicated blog post, premium placement' }
    ]
  };

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the promotion level that fits your event and budget. No hidden fees, no surprises.
          </p>
        </div>

        {/* Promotion Plans */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative bg-white rounded-2xl border-2 p-8 hover:shadow-xl transition-all ${
                plan.popular
                  ? 'border-emerald-500 shadow-lg scale-105'
                  : 'border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-4 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
                  Most Popular
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center gap-2">
                  <span className={`text-5xl font-bold ${
                    plan.color === 'emerald' ? 'text-emerald-600' :
                    plan.color === 'amber' ? 'text-amber-600' :
                    'text-gray-900'
                  }`}>
                    {plan.price}
                  </span>
                  <span className="text-gray-500">{plan.duration}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <i className={`ri-check-line text-xl ${
                      plan.color === 'emerald' ? 'text-emerald-600' :
                      plan.color === 'amber' ? 'text-amber-600' :
                      'text-gray-600'
                    } flex-shrink-0 mt-0.5`}></i>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#submit-form"
                className={`block w-full text-center py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
                  plan.popular
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                }`}
              >
                Get Started
              </a>
            </div>
          ))}
        </div>

        {/* Ticketing Commission */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <i className="ri-ticket-line"></i>
                  <span>Ticketing Platform</span>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-3">{ticketing.title}</h3>
                <p className="text-gray-600 mb-6">{ticketing.description}</p>
                <ul className="space-y-2 mb-6">
                  {ticketing.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700">
                      <i className="ri-check-line text-blue-600"></i>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center bg-white rounded-xl p-8 shadow-lg">
                <div className="text-5xl font-bold text-blue-600 mb-2">{ticketing.commission}</div>
                <div className="text-gray-600 font-medium mb-4">{ticketing.note}</div>
                <a
                  href="#submit-form"
                  className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  Enable Ticketing
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Event Sponsorship */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
              <i className="ri-vip-crown-line"></i>
              <span>For Major Events</span>
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-3">{sponsorship.title}</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">{sponsorship.description}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {sponsorship.tiers.map((tier) => (
              <div key={tier.name} className="bg-white rounded-xl p-6 border-2 border-amber-200">
                <h4 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h4>
                <div className="text-3xl font-bold text-amber-600 mb-4">{tier.price}</div>
                <p className="text-gray-600 text-sm">{tier.benefits}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <a
              href="#submit-form"
              className="inline-block bg-amber-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-amber-700 transition-colors whitespace-nowrap"
            >
              Inquire About Sponsorship
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}